import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import {
  settingsBodyTextSx,
  settingsSectionPaperSx,
  settingsShellSx,
  settingsTitleSx,
} from '../settings/settingsMuiStyles';
import { submitContactForm, type ContactCategory } from './contactApi';

const ContactPage = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<ContactCategory>('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as ContactCategory);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccess('');
    setError('');

    try {
      await submitContactForm({
        email,
        category,
        message,
        pageUrl: document.referrer || window.location.href,
      });
      setSuccess('Thanks for reaching out. We received your message.');
      setMessage('');
      if (!user?.email) {
        setEmail('');
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to send your message.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={settingsShellSx}>
      <Typography variant="h4" component="h1" gutterBottom sx={settingsTitleSx}>
        Contact
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ ...settingsBodyTextSx, mb: { xs: 2, xxl: 2.5, xxxl: 3 } }}
      >
        Report a bug, share feedback, or ask a question. Include steps to
        reproduce if something is broken.
      </Typography>

      <Paper variant="outlined" sx={{ ...settingsSectionPaperSx, mt: 0 }}>
        <Box component="form" onSubmit={(event) => void handleSubmit(event)}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="contact-category-label">Type</InputLabel>
            <Select
              labelId="contact-category-label"
              id="contact-category"
              label="Type"
              value={category}
              onChange={handleCategoryChange}
            >
              <MenuItem value="bug">Bug report</MenuItem>
              <MenuItem value="feedback">Feedback</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Your email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            required
            fullWidth
            multiline
            minRows={6}
            placeholder="What went wrong, and what did you expect? If the issue was on another page, paste that page's URL."
            sx={{ mb: 2 }}
          />

          <Button type="submit" variant="contained" fullWidth disabled={submitting}>
            {submitting ? 'Sending…' : 'Send message'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContactPage;
